import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowTurnBackwardIcon,
  CursorMagicSelection02Icon,
  Delete02Icon,
  Download04Icon,
  Eraser01Icon,
  FloppyDiskIcon,
  PaintBrush02Icon,
  Location01Icon,
  Upload04Icon,
} from '@hugeicons/core-free-icons'
import { useEffect, useRef, useState } from 'react'

import { PlacementEditor, type EditorMode, type Selection } from '@/placement/editor-core'
import { fetchLibrary } from '@/placement/library'
import { assetThumbnail } from '@/placement/preview'
import type { LibraryAsset, Layout } from '@/placement/types'
import { useAppStore } from '@/store'
import { cn } from '@/lib/utils'

const SITE = 'diriyah'

// Sensible starting height (metres) per pack; the slider takes it from there.
const DEFAULT_SIZE_M: Record<string, number> = {
  plants: 8,
  furniture: 1.2,
  cars: 4.6,
  exterior: 6,
  lights: 3.5,
}

// Which packs get random yaw and size scatter. Organic things read as wrong
// when identical; manufactured things read as wrong when they are NOT, so a
// car or a bench takes the exact angle the panel is set to.
const ORGANIC_PACKS = new Set(['plants'])

function Slider(props: {
  label: string
  value: number
  display: string
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
}) {
  return (
    <label className="flex shrink-0 flex-col gap-1">
      <span className="text-muted-foreground flex items-center justify-between text-[11px] font-medium">
        {props.label}
        <span className="text-foreground tabular-nums">{props.display}</span>
      </span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
        className="accent-primary w-full cursor-pointer"
      />
    </label>
  )
}

// Authoring panel, dev-only behind ?layout=edit. Save writes
// public/layouts/<site>.json through the dev server; visitors only ever load
// that file — none of this panel ships into their path.
export function PlacementPanel() {
  const viewer = useAppStore((s) => s.viewer)
  const loaded = useAppStore((s) => s.loaded)
  const [editor, setEditor] = useState<PlacementEditor | null>(null)
  const [library, setLibrary] = useState<LibraryAsset[]>([])
  const [mode, setMode] = useState<EditorMode>('brush')
  const [pack, setPack] = useState<string>('plants')
  const [active, setActive] = useState<LibraryAsset | null>(null)
  const [thumb, setThumb] = useState<string | null>(null)
  const [counts, setCounts] = useState<{ asset: string; count: number }[]>([])
  const [canUndo, setCanUndo] = useState(false)
  const [radius, setRadius] = useState(8)
  const [density, setDensity] = useState(3)
  const [sizeM, setSizeM] = useState(8)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [nudgeStep, setNudgeStep] = useState(0.5)
  const [rotStep, setRotStep] = useState(5)
  const [modelOffset, setModelOffset] = useState<[number, number, number]>([0, 0, 0])
  const [modelRotation, setModelRotation] = useState(0)
  const [placeRotation, setPlaceRotation] = useState(0)
  const [selection, setSelection] = useState<Selection | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!loaded || !viewer) return
    const context = viewer.getPlacementContext()
    if (!context) return

    let disposed = false
    const instance = new PlacementEditor(context, () => {
      if (disposed) return
      setCounts(instance.counts())
      setCanUndo(instance.canUndo)
      setModelOffset(instance.modelOffsetM)
      setModelRotation(instance.modelRotationDeg)
      setSelection(instance.selection ? { ...instance.selection } : null)
      setSaveState('idle')
      viewer.setCameraFrozen(false)
    })
    instance.mode = 'brush'
    setEditor(instance)
    // The saved layout comes back into the editor on open — without this,
    // opening fresh and hitting Save would overwrite the committed scene
    // with an empty one. (Its runtime copy hides while the editor is open,
    // or every imported item would render twice.)
    void Promise.all([
      fetchLibrary(),
      fetch(`/layouts/${SITE}.json`).then((res) => (res.ok ? (res.json() as Promise<Layout>) : null)).catch(() => null),
    ]).then(([assets, existing]) => {
      if (disposed) return
      setLibrary(assets)
      if (existing) void instance.importLayout(existing, assets)
    })

    // The camera freeze has to engage on pointerdown, before OrbitControls
    // sees the drag; polling isPainting after the fact would fight it.
    const el = context.domElement
    const freezeIfPainting = () => {
      queueMicrotask(() => {
        if (instance.isPainting) viewer.setCameraFrozen(true)
      })
    }
    el.addEventListener('pointerdown', freezeIfPainting)

    return () => {
      disposed = true
      el.removeEventListener('pointerdown', freezeIfPainting)
      instance.dispose()
      setEditor(null)
    }
  }, [loaded, viewer])

  useEffect(() => {
    if (!editor) return
    editor.mode = mode
    editor.brush.radiusM = radius
    editor.brush.perStamp = density
    editor.brush.sizeM = sizeM
    editor.brush.rotationJitter = active ? ORGANIC_PACKS.has(active.pack) : true
    editor.placeRotationDeg = placeRotation
  }, [editor, mode, radius, density, sizeM, active, placeRotation])

  if (!editor) return null

  const packs = [...new Set(library.map((a) => a.pack))]

  const pick = (asset: LibraryAsset) => {
    setActive(asset)
    editor.setActiveAsset(asset)
    setSizeM(DEFAULT_SIZE_M[asset.pack] ?? 2)
    setThumb(null)
    void assetThumbnail(asset).then((url) => setThumb(url))
  }

  const save = () => {
    setSaveState('saving')
    fetch('/api/layout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ site: SITE, layout: editor.exportLayout(SITE) }),
    })
      .then((res) => setSaveState(res.ok ? 'saved' : 'failed'))
      .catch(() => setSaveState('failed'))
  }

  const exportLayout = () => {
    const layout = editor.exportLayout(SITE)
    const blob = new Blob([JSON.stringify(layout, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${SITE}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const importLayout = (file: File) => {
    void file.text().then((text) => {
      void editor.importLayout(JSON.parse(text) as Layout, library)
    })
  }

  const total = counts.reduce((n, c) => n + c.count, 0)

  return (
    <div className="glass pointer-events-auto fixed top-20 right-4 z-90 flex max-h-[calc(100vh-10rem)] w-80 flex-col overflow-hidden p-0">
      {/* Header */}
      <div className="flex shrink-0 items-baseline justify-between px-4 pt-3.5 pb-2.5">
        <span className="text-sm font-semibold">Layout editor</span>
        <span className="text-muted-foreground text-xs tabular-nums">
          {total > 0 ? `${total} items` : 'empty'}
        </span>
      </div>

      {/* Mode switch */}
      <div className="bg-foreground/4 mx-4 flex shrink-0 gap-0.5 rounded-xl p-0.5">
        {(
          [
            ['brush', PaintBrush02Icon, 'Scatter'],
            ['place', Location01Icon, 'Place'],
            ['select', CursorMagicSelection02Icon, 'Select'],
            ['erase', Eraser01Icon, 'Erase'],
          ] as const
        ).map(([value, icon, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            aria-pressed={mode === value}
            className={cn(
              'flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-[10px] py-1.5 text-[11px] font-medium transition-colors',
              mode === value
                ? 'bg-white/85 text-foreground shadow-sm'
                : 'text-foreground/55 hover:text-foreground',
            )}
          >
            <HugeiconsIcon icon={icon} size={13} strokeWidth={2} />
            {label}
          </button>
        ))}
      </div>

      {/* Active asset: the preview is the hero — you should never have to
          squint at what the brush is loaded with. */}
      {mode !== 'erase' && (
        <div className="mx-4 mt-3 shrink-0">
          <div
            className={cn(
              'bg-black/5 h-40 w-full overflow-hidden rounded-xl',
              !active && 'border-foreground/15 border border-dashed bg-transparent',
            )}
          >
            {active && thumb ? (
              <img src={thumb} alt="" className="size-full object-contain" />
            ) : (
              <div className="text-muted-foreground flex size-full items-center justify-center text-xs">
                {active ? 'Rendering preview…' : 'Pick an asset below'}
              </div>
            )}
          </div>
          {active && (
            <div className="mt-1.5 flex items-baseline justify-between text-xs">
              <p className="truncate text-[13px] font-semibold">
                {active.name.replace(/_/g, ' ')}
              </p>
              <p className="text-muted-foreground ml-2 shrink-0">
                {(active.tris / 1000).toFixed(1)}k tris · {active.kb} KB ·{' '}
                <span className="tabular-nums">{sizeM.toFixed(1)} m</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Params */}
      <div className="mx-4 mt-3 flex shrink-0 flex-col gap-2">
        {mode !== 'place' && (
          <Slider
            label="Brush radius"
            value={radius}
            display={`${radius} m`}
            min={2}
            max={30}
            onChange={setRadius}
          />
        )}
        {mode === 'brush' && (
          <Slider
            label="Density"
            value={density}
            display={String(density)}
            min={1}
            max={10}
            onChange={setDensity}
          />
        )}
        {mode !== 'erase' && mode !== 'select' && (
          <Slider
            label="Item size"
            value={sizeM}
            display={`${sizeM.toFixed(1)} m`}
            min={0.2}
            max={20}
            step={0.1}
            onChange={setSizeM}
          />
        )}
        {/* Manufactured assets place at an exact heading; organic ones are
            randomised and the control would be a lie. */}
        {active && !ORGANIC_PACKS.has(active.pack) && mode !== 'erase' && mode !== 'select' && (
          <Slider
            label="Heading"
            value={placeRotation}
            display={`${placeRotation}°`}
            min={0}
            max={359}
            onChange={setPlaceRotation}
          />
        )}
      </div>

      {/* Selected item: the after-the-fact control the brush cannot give you. */}
      {mode === 'select' && (
        <div className="border-foreground/8 mx-4 mt-3 shrink-0 border-t pt-3">
          {selection ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="truncate text-xs font-semibold">
                  {selection.asset.split('/')[1]?.replace(/_/g, ' ')}
                </span>
                <button
                  type="button"
                  onClick={() => editor.deleteSelection()}
                  className="text-destructive cursor-pointer text-[11px] font-medium"
                >
                  Delete
                </button>
              </div>
              <Slider
                label="Rotation"
                value={Math.round(selection.rotationDeg)}
                display={`${Math.round(selection.rotationDeg)}°`}
                min={0}
                max={359}
                onChange={(v) => editor.setSelectionRotation(v)}
              />
              <Slider
                label="Size"
                value={selection.sizeM}
                display={`${selection.sizeM.toFixed(1)} m`}
                min={0.2}
                max={20}
                step={0.1}
                onChange={(v) => editor.setSelectionSize(v)}
              />
              <div className="grid grid-cols-3 gap-1 text-[11px]">
                {(
                  [
                    ['X −', -1, 0, 0],
                    ['Y ↑', 0, 1, 0],
                    ['Z −', 0, 0, -1],
                    ['X +', 1, 0, 0],
                    ['Y ↓', 0, -1, 0],
                    ['Z +', 0, 0, 1],
                  ] as const
                ).map(([label, dx, dy, dz]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => editor.moveSelection(dx * nudgeStep, dy * nudgeStep, dz * nudgeStep)}
                    className="surface-chip rounded-control cursor-pointer py-1.5 font-medium tabular-nums"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                {[0.1, 0.5, 2].map((step) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setNudgeStep(step)}
                    aria-pressed={nudgeStep === step}
                    className={cn(
                      'rounded-control cursor-pointer px-2 py-1 text-[11px] tabular-nums transition-colors',
                      nudgeStep === step ? 'bg-primary text-primary-foreground' : 'surface-chip',
                    )}
                  >
                    {step} m
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => editor.dropSelection()}
                  title="Drop onto the surface below"
                  className="surface-chip rounded-control ml-auto cursor-pointer px-2.5 py-1 text-[11px] font-medium"
                >
                  Drop to ground
                </button>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">Click a placed item to select it.</p>
          )}
        </div>
      )}

      {/* Model alignment: nudge the host model until it sits right in the
          scene. World axes, metres; persisted with the layout. */}
      <details className="mx-4 mt-3 shrink-0">
        <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-[11px] font-semibold tracking-wide uppercase select-none">
          Model position
          {(modelOffset.some((v) => v !== 0) || modelRotation !== 0) && (
            <span className="text-primary ml-1.5 normal-case">
              {modelOffset.map((v) => v.toFixed(1)).join(', ')} m · {modelRotation.toFixed(1)}°
            </span>
          )}
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-1">
            {[0.1, 0.5, 2].map((step) => (
              <button
                key={step}
                type="button"
                onClick={() => setNudgeStep(step)}
                aria-pressed={nudgeStep === step}
                className={cn(
                  'rounded-control cursor-pointer px-2 py-1 text-[11px] tabular-nums transition-colors',
                  nudgeStep === step ? 'bg-primary text-primary-foreground' : 'surface-chip',
                )}
              >
                {step} m
              </button>
            ))}
            <span className="text-muted-foreground ml-auto text-[10px]">world axes</span>
          </div>
          <div className="grid grid-cols-3 gap-1 text-[11px]">
            {(
              [
                ['X −', -1, 0, 0],
                ['Y ↑', 0, 1, 0],
                ['Z −', 0, 0, -1],
                ['X +', 1, 0, 0],
                ['Y ↓', 0, -1, 0],
                ['Z +', 0, 0, 1],
              ] as const
            ).map(([label, dx, dy, dz]) => (
              <button
                key={label}
                type="button"
                onClick={() => editor.nudgeModel(dx * nudgeStep, dy * nudgeStep, dz * nudgeStep)}
                className="surface-chip rounded-control cursor-pointer py-1.5 font-medium tabular-nums"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[0.5, 5, 45].map((step) => (
              <button
                key={step}
                type="button"
                onClick={() => setRotStep(step)}
                aria-pressed={rotStep === step}
                className={cn(
                  'rounded-control cursor-pointer px-2 py-1 text-[11px] tabular-nums transition-colors',
                  rotStep === step ? 'bg-primary text-primary-foreground' : 'surface-chip',
                )}
              >
                {step}°
              </button>
            ))}
            <button
              type="button"
              onClick={() => editor.rotateModel(rotStep)}
              title="Rotate counter-clockwise"
              className="surface-chip rounded-control ml-auto cursor-pointer px-3 py-1 text-[13px]"
            >
              ⟲
            </button>
            <button
              type="button"
              onClick={() => editor.rotateModel(-rotStep)}
              title="Rotate clockwise"
              className="surface-chip rounded-control cursor-pointer px-3 py-1 text-[13px]"
            >
              ⟳
            </button>
          </div>
        </div>
      </details>

      {/* Pack tabs */}
      <div className="mx-4 mt-3 flex shrink-0 gap-3">
        {packs.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPack(p)}
            aria-pressed={pack === p}
            className={cn(
              'cursor-pointer border-b-2 pb-1 text-[11px] font-semibold tracking-wide uppercase transition-colors',
              pack === p
                ? 'border-primary text-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground',
            )}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Palette: the ONE scrolling region, so it can never paint over the
          sections below it (the old min-h-0 flex shrink did exactly that). */}
      <div className="mx-4 mt-2 min-h-24 flex-1 overflow-y-auto">
        {library.length === 0 ? (
          <p className="text-muted-foreground py-2 text-xs">Loading library from CDN…</p>
        ) : (
          <div className="flex flex-wrap content-start gap-1 pb-2">
            {library
              .filter((a) => a.pack === pack)
              .map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => pick(a)}
                  aria-pressed={active?.id === a.id}
                  title={`${a.tris.toLocaleString()} tris · ${a.kb} KB`}
                  className={cn(
                    'rounded-control cursor-pointer px-2 py-1 text-[11px] transition-colors',
                    active?.id === a.id
                      ? 'bg-primary text-primary-foreground'
                      : 'surface-chip',
                  )}
                >
                  {a.name.replace(/_/g, ' ')}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Layers */}
      {counts.length > 0 && (
        <div className="border-foreground/8 mx-4 flex max-h-28 shrink-0 flex-col gap-1 overflow-y-auto border-t pt-2">
          {counts.map(({ asset, count }) => (
            <div key={asset} className="flex items-center justify-between gap-2 text-xs">
              <span className="text-muted-foreground truncate">
                {asset.split('/')[1]?.replace(/_/g, ' ') ?? asset}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="tabular-nums">{count}</span>
                <button
                  type="button"
                  onClick={() => editor.clearLayer(asset)}
                  aria-label={`Clear ${asset}`}
                  className="text-foreground/35 hover:text-destructive cursor-pointer"
                >
                  <HugeiconsIcon icon={Delete02Icon} size={13} strokeWidth={2} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="border-foreground/8 mt-2 flex shrink-0 items-center gap-1.5 border-t px-4 py-3">
        <button
          type="button"
          onClick={() => editor.undo()}
          disabled={!canUndo}
          title="Undo stroke"
          className="surface-chip rounded-control flex size-8 cursor-pointer items-center justify-center disabled:cursor-default disabled:opacity-35"
        >
          <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          title="Import layout JSON"
          className="surface-chip rounded-control flex size-8 cursor-pointer items-center justify-center"
        >
          <HugeiconsIcon icon={Upload04Icon} size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={exportLayout}
          title="Export layout JSON"
          className="surface-chip rounded-control flex size-8 cursor-pointer items-center justify-center"
        >
          <HugeiconsIcon icon={Download04Icon} size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saveState === 'saving'}
          title={`Writes public/layouts/${SITE}.json — the scene visitors load`}
          className={cn(
            'rounded-control ml-auto flex h-8 cursor-pointer items-center justify-center gap-1.5 px-4 text-xs font-semibold transition-colors',
            saveState === 'saved'
              ? 'bg-emerald-600 text-white'
              : saveState === 'failed'
                ? 'bg-destructive text-white'
                : 'bg-primary text-primary-foreground hover:opacity-90',
          )}
        >
          <HugeiconsIcon icon={FloppyDiskIcon} size={13} strokeWidth={2} />
          {saveState === 'saving'
            ? 'Saving…'
            : saveState === 'saved'
              ? 'Saved'
              : saveState === 'failed'
                ? 'Failed'
                : 'Save'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) importLayout(file)
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
